import { DownloadIcon, EyeIcon } from '@honestica/curecall-ui'
import { twMerge } from 'src/utils/tw-merge.util'
import { DocumentName } from './document-name.component'
import { DocumentStatusPill } from './document-status-pill.component'
import { DocumentSubtitle } from './document-subtitle.component'

export interface DocumentCardActions {
  previewLabel: string
  downloadLabel: string
  isDownloading: boolean
  onPreview: VoidFunction
  onDownload: VoidFunction
}

interface DocumentCardProps {
  name: string
  statusLabel: string
  isStatusAvailable: boolean
  subtitle?: string | null
  isSelected?: boolean
  /** A card without actions has nothing to open or download, so it is not interactive. */
  actions?: DocumentCardActions
}

const EMPHASIZED_CARD_CLASS = 'c-border-document-action/30 c-shadow-document-card'

const ACTION_BUTTON_CLASS =
  'c-flex c-size-8 c-shrink-0 c-items-center c-justify-center c-rounded-lg c-border c-border-grey-200 c-bg-white c-text-grey-700'

const DocumentCard = ({
  name,
  statusLabel,
  isStatusAvailable,
  subtitle,
  isSelected = false,
  actions,
}: DocumentCardProps) => (
  <div
    data-testid="document-card"
    onClick={actions?.onPreview}
    className={twMerge(
      'c-grid c-items-start c-gap-x-2 c-gap-y-1 c-rounded-lg c-border c-border-black/[0.08] c-bg-white c-p-4 c-transition',
      actions ? 'c-grid-cols-[auto_minmax(0,1fr)_auto]' : 'c-grid-cols-[auto_minmax(0,1fr)]',
      actions &&
        'c-cursor-pointer c-select-none hover:c-border-document-action/30 hover:c-shadow-document-card',
      isSelected && EMPHASIZED_CARD_CLASS,
    )}
  >
    <div className={twMerge('c-flex c-items-center', actions && 'c-h-8')}>
      <DocumentStatusPill label={statusLabel} isAvailable={isStatusAvailable} />
    </div>
    <DocumentName className={twMerge('c-flex c-items-center', actions && 'c-min-h-8')}>{name}</DocumentName>
    {actions && (
      <div className="c-flex c-shrink-0 c-items-center c-gap-2">
        <button
          type="button"
          aria-label={actions.previewLabel}
          aria-pressed={isSelected}
          className={twMerge(
            ACTION_BUTTON_CLASS,
            isSelected && 'c-border-document-action c-text-document-action',
          )}
        >
          <EyeIcon className="c-size-4" />
        </button>
        <button
          type="button"
          aria-label={actions.downloadLabel}
          disabled={actions.isDownloading}
          onClick={(event) => {
            event.stopPropagation()
            actions.onDownload()
          }}
          className={twMerge(
            ACTION_BUTTON_CLASS,
            'hover:c-border-document-action/30 hover:c-text-document-action',
            actions.isDownloading && 'c-opacity-50',
          )}
        >
          <DownloadIcon className="c-size-4" />
        </button>
      </div>
    )}
    {subtitle && (
      <div className="c-col-start-2 c-col-end-[-1]">
        <DocumentSubtitle>{subtitle}</DocumentSubtitle>
      </div>
    )}
  </div>
)

export { DocumentCard }
